import type { PerformanceResourceItem } from '@/app/lib/api/types-api'
import { ALL_LOCATIONS } from '@/app/lib/constants'
import { routes } from '@/app/lib/routing'

import { EXTRA_AGES, EXTRA_GATES, EXTRA_HANDICAPPED, EXTRA_SHOW_LENGTH } from './constants'
import { extraText } from './extraText'
import { resolveArtistLinks } from './resolveArtistLinks'
import type { HeroArtist } from './resolveArtistLinks'
import { resolvePickupOptions } from './resolvePickupOptions'
import type { PickupOption } from './resolvePickupOptions'

/**
 * Un valor enlazable de una fila `links`. `href` es `null` cuando el dato no
 * trae con qué construir la URL: se pinta como texto plano, no como enlace roto.
 */
export interface HighlightLink {
  readonly id: string
  readonly label: string
  readonly href: string | null
}

/**
 * Una fila de la tabla de información del evento. El `kind` gobierna cómo se
 * pinta el valor, no qué significa la fila.
 */
export type PerformanceHighlightRow =
  | {
      readonly id: string
      readonly label: string
      readonly kind: 'artists'
      readonly artists: readonly HeroArtist[]
    }
  | {
      readonly id: string
      readonly label: string
      readonly kind: 'pickup'
      readonly options: readonly PickupOption[]
    }
  /** Varios valores enlazados en línea, separados por «·» (Categoría). */
  | {
      readonly id: string
      readonly label: string
      readonly kind: 'links'
      readonly links: readonly HighlightLink[]
    }
  | { readonly id: string; readonly label: string; readonly kind: 'text'; readonly text: string }
  /** Texto largo: se recorta a 3 líneas con «Ver más» (FR-038). */
  | {
      readonly id: string
      readonly label: string
      readonly kind: 'longText'
      readonly text: string
      /** Documento descargable, solo en la fila de edades (FR-039). */
      readonly pdfUrl: string | null
    }

/**
 * Categoría a partir de `genres[]` (FR-033): `name` + `style_name` cuando la
 * subcategoría existe, solo `name` si no. El payload real trae eventos con
 * hasta cinco géneros, y se deduplican por etiqueta.
 *
 * Cada género enlaza a su listado, usando los builders centralizados de
 * `lib/routing` (nunca la URL a mano): con subcategoría, a la PLP de
 * subcategoría sin provincia (`ALL_LOCATIONS`); sin ella, a la landing de la
 * categoría — la misma forma que ya usan las migas de la PDP.
 *
 * Sin `nicename` no hay URL que construir: la etiqueta viaja con `href: null` y
 * el consumidor la pinta como texto plano.
 */
function resolveCategoryLinks(resource: PerformanceResourceItem): HighlightLink[] {
  const links: HighlightLink[] = []
  const seen = new Set<string>()

  for (const genre of resource.genres ?? []) {
    const name = genre.name?.trim()
    if (!name) continue

    const style = genre.style_name?.trim()
    const label = style ? `${name} – ${style}` : name
    if (seen.has(label)) continue
    seen.add(label)

    const categoria = genre.nicename?.trim()
    const subcategoria = genre.style_nicename?.trim()
    const href = categoria
      ? style && subcategoria
        ? routes.subcategoria(categoria, ALL_LOCATIONS, subcategoria)
        : routes.page(categoria)
      : null

    links.push({ id: label, label, href })
  }

  return links
}

/**
 * Filas de la tabla de información del evento, en el **orden fijo** que exige
 * FR-030: artistas → recogida → duración → categoría → apertura de puertas →
 * organizador → movilidad reducida → edades.
 *
 * Las filas sin dato no se incluyen: el consumidor pinta lo que reciba, sin
 * decidir nada. Así una ficha mínima no muestra etiquetas huérfanas (FR-004).
 *
 * La fila de artistas lista **todos**, no solo los dos de la cabecera — es el
 * destino del enlace «+X más» (FR-031), así que recortarla aquí dejaría ese
 * enlace sin sentido.
 */
export function toPerformanceHighlightRows(
  resource: PerformanceResourceItem,
  /** `id` de la fila de artistas, para que «+X más» pueda anclarla (FR-057). */
  artistsAnchorId: string,
): PerformanceHighlightRow[] {
  const rows: PerformanceHighlightRow[] = []

  const artists = resolveArtistLinks(resource).all
  if (artists.length > 0) {
    rows.push({ id: artistsAnchorId, label: 'Artistas', kind: 'artists', artists })
  }

  const pickup = resolvePickupOptions(resource)
  if (pickup.length > 0) {
    rows.push({ id: 'recogida', label: 'Recogida de entradas', kind: 'pickup', options: pickup })
  }

  const duration = extraText(resource, EXTRA_SHOW_LENGTH)
  if (duration) {
    rows.push({ id: 'duracion', label: 'Duración', kind: 'text', text: duration })
  }

  const categoryLinks = resolveCategoryLinks(resource)
  if (categoryLinks.length > 0) {
    rows.push({ id: 'categoria', label: 'Categoría', kind: 'links', links: categoryLinks })
  }

  const gates = extraText(resource, EXTRA_GATES)
  if (gates) {
    rows.push({ id: 'apertura', label: 'Apertura de puertas', kind: 'text', text: gates })
  }

  const promoterName = resource.promoter?.name?.trim()
  if (promoterName) {
    const promoterNif = resource.promoter?.nif?.trim()
    const promoter = promoterNif ? `${promoterName} (CIF ${promoterNif})` : promoterName
    rows.push({ id: 'organizador', label: 'Organizador', kind: 'text', text: promoter })
  }

  const handicapped = extraText(resource, EXTRA_HANDICAPPED)
  if (handicapped) {
    rows.push({
      id: 'movilidad',
      label: 'Movilidad reducida',
      kind: 'longText',
      text: handicapped,
      pdfUrl: null,
    })
  }

  const ages = extraText(resource, EXTRA_AGES)
  const pdfUrl = resource.pdf_extras?.trim() || null
  if (ages || pdfUrl) {
    rows.push({
      id: 'edades',
      label: 'Edades',
      kind: 'longText',
      text: ages ?? '',
      pdfUrl,
    })
  }

  return rows
}
